import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, DatePicker, Drawer } from 'antd'
import { CalendarOutlined, CloseCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { now } from '../../utils/timezone'

/**
 * 手機／平板改用「彈出式年月日」的 DatePicker
 *
 * 為什麼要做這個：
 *   antd 的日曆浮層是為滑鼠設計的 —— 一個月格一格 32px，手指很容易點錯隔天；
 *   跨年、跨好幾個月時還得一直點左右箭頭翻頁。設定堂票有效期限這種
 *   「直接知道是哪一天」的情境，翻日曆比選年月日慢得多。
 *
 *   所以在 <=1024px（含 iPad 橫向）換成底部抽屜，年／月／日三欄各自捲動，
 *   每個選項是 48px 的觸控目標，選完按「確定」。桌機維持原本的 antd
 *   DatePicker，完全不變（和 ResponsiveSelect 同一套做法）。
 *
 * 用法與 antd DatePicker 相容：
 *   - value / onChange 都是 dayjs（和 antd 一樣是瀏覽器牆上時間，
 *     呼叫端照原本的習慣用 fromPicker() 轉店家時區）
 *   - placeholder / disabled / allowClear / size / style / format
 *   - disabledDate(day) 也吃：日會變灰，整月／整年都被擋的話月份變灰、年份不列出
 *   - 其餘 props（inputReadOnly、getPopupContainer…）只在桌機透傳
 *
 * 只處理日期；要選時間請繼續用 antd 的 DatePicker showTime。
 */

const Trigger = styled.button`
  width: 100%;
  min-height: ${({ $size }) => ($size === 'large' ? '48px' : '40px')};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 11px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 16px; /* iOS 聚焦不放大的門檻 */
  color: ${({ $placeholder }) => ($placeholder ? '#bfbfbf' : 'rgba(0,0,0,0.88)')};
  text-align: left;
  cursor: pointer;

  &:disabled {
    background: #f5f5f5;
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }

  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .icon {
    flex: none;
    color: rgba(0, 0, 0, 0.25);
    font-size: 13px;
  }
`

const Preview = styled.div`
  padding: 4px 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #c9637d;
  text-align: center;
`

const Columns = styled.div`
  display: flex;
  gap: 8px;
  border-top: 1px solid #f0f0f0;
`

const Column = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const ColumnHead = styled.div`
  padding: 8px 0 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #8c8c8c;
  text-align: center;
`

const ColumnList = styled.div`
  max-height: 44vh;
  overflow-y: auto;
  /* 捲到底不要把動量交給整頁 —— 這正是原本難滑的主因 */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`

const Cell = styled.button`
  width: 100%;
  min-height: 48px;
  display: block;
  padding: 12px 4px;
  background: ${({ $active }) => ($active ? '#fdf0f3' : 'transparent')};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  color: ${({ $active }) => ($active ? '#c9637d' : 'rgba(0,0,0,0.88)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: center;
  cursor: pointer;

  &:active:not(:disabled) {
    background: #f7f7f9;
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.22);
    cursor: not-allowed;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;

  .ant-btn {
    flex: 1;
    height: 44px;
  }
`

/** 以 y/m/d 組出一個 dayjs（m 是 0-based，和 dayjs 一致） */
const makeDay = (y, m, d) => dayjs(new Date(y, m, d))

/**
 * 店家時區的「今天」，但以瀏覽器牆上時間表示（和 value 同一種值）。
 * 直接用 dayjs() 的話，店家時區≠瀏覽器時區時預設會落在錯的那一天。
 */
const storeToday = () => {
  const n = now()
  return makeDay(n.year(), n.month(), n.date())
}

const ResponsiveDatePicker = ({
  value,
  onChange,
  placeholder = '選擇日期',
  disabled = false,
  allowClear = false,
  size,
  style,
  className,
  format = 'YYYY-MM-DD',
  disabledDate,
  drawerTitle,
  /** 年份清單的範圍（相對今年）；選中的年份一定會被包含 */
  yearsBefore = 5,
  yearsAfter = 10,
  ...rest
}) => {
  const [isTouchLayout, setIsTouchLayout] = useState(false)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(() => storeToday())
  const listRefs = useRef({})

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => setIsTouchLayout(window.innerWidth <= 1024)
    sync()
    window.addEventListener('resize', sync)
    window.addEventListener('orientationchange', sync)
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('orientationchange', sync)
    }
  }, [])

  const isDayDisabled = useCallback(
    (d) => (disabledDate ? !!disabledDate(d) : false),
    [disabledDate]
  )

  /** 整個月都不能選 → 月份直接變灰（例如「今天以前不給選」的當年前幾個月） */
  const isMonthDisabled = useCallback(
    (y, m) => {
      const start = makeDay(y, m, 1)
      const len = start.daysInMonth()
      for (let i = 0; i < len; i++) {
        if (!isDayDisabled(start.add(i, 'day'))) return false
      }
      return true
    },
    [isDayDisabled]
  )

  const years = useMemo(() => {
    const thisYear = now().year()
    const from = thisYear - yearsBefore
    const to = thisYear + yearsAfter
    const set = new Set()
    for (let y = from; y <= to; y++) set.add(y)
    // 編輯舊資料時，選中的年份可能落在範圍外，一定要列出來
    if (value?.isValid?.()) set.add(value.year())
    return [...set]
      .sort((a, b) => a - b)
      .filter((y) => {
        if (value?.isValid?.() && y === value.year()) return true
        for (let m = 0; m < 12; m++) if (!isMonthDisabled(y, m)) return true
        return false
      })
  }, [value, yearsBefore, yearsAfter, isMonthDisabled])

  const daysInDraftMonth = draft.daysInMonth()

  // 開啟時以現有值（沒有就用今天）當草稿，並把三欄捲到選中的位置
  useEffect(() => {
    if (!open) return
    setDraft(value?.isValid?.() ? value : storeToday())
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => {
      Object.values(listRefs.current).forEach((el) => {
        if (!el) return
        const active = el.querySelector('[data-active="1"]')
        if (active) {
          el.scrollTop =
            active.offsetTop - el.clientHeight / 2 + active.clientHeight / 2
        }
      })
    }, 50)
    return () => clearTimeout(t)
  }, [open])

  /**
   * 換年／換月之後，原本的日可能不存在（1/31 → 2 月）或剛好被擋掉，
   * 自動挪到當月第一個可選的日子，使用者才不會按下「確定」卻沒反應。
   */
  const moveDraft = (next) => {
    let d = next
    if (d.date() > d.daysInMonth()) d = d.date(d.daysInMonth())
    if (isDayDisabled(d)) {
      const start = d.startOf('month')
      const len = start.daysInMonth()
      for (let i = 0; i < len; i++) {
        const candidate = start.add(i, 'day')
        if (!isDayDisabled(candidate)) {
          d = candidate
          break
        }
      }
    }
    setDraft(d)
  }

  // 桌機維持原本的 antd DatePicker
  if (!isTouchLayout) {
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        size={size}
        style={style}
        className={className}
        format={format}
        disabledDate={disabledDate}
        {...rest}
      />
    )
  }

  const hasValue = !!value?.isValid?.()
  const confirmDisabled = isDayDisabled(draft)

  return (
    <>
      <Trigger
        type='button'
        className={className}
        style={style}
        $size={size}
        $placeholder={!hasValue}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <span className='label'>
          {hasValue ? value.format(format) : placeholder}
        </span>
        {allowClear && hasValue && (
          <CloseCircleFilled
            className='icon'
            onClick={(e) => {
              // 不要順帶把抽屜打開
              e.stopPropagation()
              onChange?.(null, '')
            }}
          />
        )}
        <CalendarOutlined className='icon' />
      </Trigger>

      <Drawer
        placement='bottom'
        open={open}
        onClose={() => setOpen(false)}
        title={drawerTitle || placeholder}
        height='auto'
        styles={{
          body: { padding: '0 16px 16px' },
          content: { borderRadius: '16px 16px 0 0' }
        }}
      >
        <Preview>{draft.format('YYYY 年 M 月 D 日')}</Preview>

        <Columns>
          <Column>
            <ColumnHead>年</ColumnHead>
            <ColumnList ref={(el) => (listRefs.current.year = el)}>
              {years.map((y) => {
                const active = y === draft.year()
                return (
                  <Cell
                    key={y}
                    type='button'
                    data-active={active ? '1' : '0'}
                    $active={active}
                    onClick={() => moveDraft(draft.year(y))}
                  >
                    {y}
                  </Cell>
                )
              })}
            </ColumnList>
          </Column>

          <Column>
            <ColumnHead>月</ColumnHead>
            <ColumnList ref={(el) => (listRefs.current.month = el)}>
              {Array.from({ length: 12 }, (_, m) => {
                const active = m === draft.month()
                return (
                  <Cell
                    key={m}
                    type='button'
                    data-active={active ? '1' : '0'}
                    $active={active}
                    disabled={isMonthDisabled(draft.year(), m)}
                    onClick={() => moveDraft(draft.month(m))}
                  >
                    {m + 1}
                  </Cell>
                )
              })}
            </ColumnList>
          </Column>

          <Column>
            <ColumnHead>日</ColumnHead>
            <ColumnList ref={(el) => (listRefs.current.day = el)}>
              {Array.from({ length: daysInDraftMonth }, (_, i) => {
                const d = i + 1
                const active = d === draft.date()
                return (
                  <Cell
                    key={d}
                    type='button'
                    data-active={active ? '1' : '0'}
                    $active={active}
                    disabled={isDayDisabled(draft.date(d))}
                    onClick={() => setDraft(draft.date(d))}
                  >
                    {d}
                  </Cell>
                )
              })}
            </ColumnList>
          </Column>
        </Columns>

        <Footer>
          <Button onClick={() => setOpen(false)}>取消</Button>
          <Button
            type='primary'
            disabled={confirmDisabled}
            onClick={() => {
              setOpen(false)
              onChange?.(draft, draft.format(format))
            }}
          >
            確定
          </Button>
        </Footer>
      </Drawer>
    </>
  )
}

export default ResponsiveDatePicker
